import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiBox, FiChevronDown, FiMinus, FiPlus, FiSearch, FiShoppingBag, FiUser, FiX } from 'react-icons/fi';
import { MdOutlineReceiptLong } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useSaasSession } from '../../saas/SessionContext';
import { nextTenantNumber, useTenantNumbering } from '../../saas/tenantNumbering';
import './MobilePos.css';

const money = (amount) => Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

function MobilePos() {
  const navigate = useNavigate();
  const { user, membership, tenant, logout } = useSaasSession();
  const numbering = useTenantNumbering();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesCount, setSalesCount] = useState(0);
  const [query, setQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [manualInvoice, setManualInvoice] = useState('');

  const canSell = membership?.role === 'owner' || membership?.permissions?.sales || membership?.permissions?.addSales || membership?.role === 'manager';
  const automaticInvoice = numbering.Auto_Sales_Invoice;
  const generatedInvoice = nextTenantNumber(numbering.Sales_Invoice_Prefix, salesCount, 6);
  const salesInvoice = automaticInvoice ? generatedInvoice : manualInvoice;
  const businessName = tenant?.settings?.businessName || tenant?.name || 'POS';

  useEffect(() => {
    let active = true;
    const loadReferenceData = async () => {
      try {
        const [customerResponse, countResponse] = await Promise.all([
          fetch('/rupkotha/getmaincustomers'),
          fetch('/rupkotha/getsalescount'),
        ]);
        const [customerData, countData] = await Promise.all([
          customerResponse.json().catch(() => []),
          countResponse.json().catch(() => ({ result: 0 })),
        ]);
        if (!customerResponse.ok || !countResponse.ok) throw new Error('Mobile POS data could not be loaded.');
        if (!active) return;
        setCustomers(Array.isArray(customerData) ? customerData : []);
        setSalesCount(Number(countData?.result || 0));
      } catch (error) {
        toast.error(error.message || 'Mobile POS data could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    };
    loadReferenceData();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(`/rupkotha/getproductsforstock?searchTerm=${encodeURIComponent(query)}&limit=60`);
        const productData = await response.json().catch(() => []);
        if (!response.ok) throw new Error('Products could not be loaded.');
        if (active) setProducts(Array.isArray(productData) ? productData : []);
      } catch (error) {
        if (active) toast.error(error.message || 'Products could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    }, query ? 180 : 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [query]);

  const visibleProducts = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return products;
    return products.filter((product) => String(product?.name || '').toLowerCase().includes(normalized) || String(product?.code || '').toLowerCase().includes(normalized)).slice(0, 80);
  }, [products, query]);

  const visibleCustomers = useMemo(() => {
    const normalized = customerSearch.trim().toLowerCase();
    if (!normalized) return customers.slice(0, 40);
    return customers.filter((customer) => String(customer?.name || '').toLowerCase().includes(normalized) || String(customer?.phone || '').includes(normalized) || String(customer?.code || '').toLowerCase().includes(normalized)).slice(0, 40);
  }, [customers, customerSearch]);

  const cartTotal = useMemo(() => cart.reduce((sum, item) => sum + Number(item.sell || 0) * item.quantity, 0), [cart]);
  const cartQuantity = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);

  const addProduct = (product) => {
    setCart((items) => {
      const existing = items.find((item) => item._id === product._id || item.code === product.code);
      if (existing) return items.map((item) => item._id === existing._id ? { ...item, quantity: item.quantity + 1 } : item);
      return [...items, { ...product, quantity: 1 }];
    });
    setCartOpen(true);
  };

  const setQuantity = (product, nextQuantity) => {
    if (nextQuantity <= 0) {
      setCart((items) => items.filter((item) => item._id !== product._id));
      return;
    }
    setCart((items) => items.map((item) => item._id === product._id ? { ...item, quantity: nextQuantity } : item));
  };

  const completeSale = async () => {
    if (!canSell) return toast.error('Your account does not have permission to create sales.');
    if (!cart.length) return toast.error('Add at least one product to the cart.');
    if (!selectedCustomer) return toast.error('Select a customer before checkout.');
    if (!salesInvoice.trim()) return toast.error('Enter a sales invoice number.');

    const salesDate = today();
    const month = new Date(`${salesDate}T00:00:00`).toLocaleString('default', { month: 'long', year: 'numeric' });
    const totalAmount = Number(cartTotal.toFixed(2));
    const payload = {
      products: cart.map((item) => ({ ...item, total: Number((Number(item.sell || 0) * item.quantity).toFixed(2)) })),
      salesInvoice: salesInvoice.trim(),
      salesDate,
      customerCode: selectedCustomer.code || '',
      salesCustomer: selectedCustomer.name || '',
      salesAddress: selectedCustomer.address || '',
      salesPhone: selectedCustomer.phone || '',
      salesOpening: Number(selectedCustomer.opening || 0),
      totalAmount,
      discount: 0,
      fixedDis: 0,
      roundingDis: 0,
      grandTotal: totalAmount,
      payment: totalAmount,
      user: user?.email?.split('@')[0] || user?.name || '',
      dueAmount: 0,
      type: 'Pos',
      month,
      paid: true,
    };

    try {
      setSubmitting(true);
      const response = await fetch('/rupkotha/sales', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.message || 'Sale could not be completed.');
      toast.success('Sale completed successfully.');
      setCart([]);
      setSalesCount((value) => value + 1);
      setCartOpen(false);
      setCustomerPickerOpen(false);
      setSelectedCustomer(null);
    } catch (error) {
      toast.error(error.message || 'Sale could not be completed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canSell) {
    return <main className="mobile-pos mobile-pos--restricted"><div className="mobile-pos__restricted-card"><FiShoppingBag /><h1>Mobile POS is unavailable</h1><p>Your account does not have permission to add sales.</p><Link to="/">Return to dashboard</Link></div></main>;
  }

  return (
    <main className="mobile-pos">
      <header className="mobile-pos__header">
        <div className="mobile-pos__brand"><button type="button" aria-label="Return to dashboard" onClick={() => navigate('/')}><FiArrowLeft /></button><div><span>{businessName}</span><small>Mobile POS</small></div></div>
        <button type="button" className="mobile-pos__avatar" onClick={logout} aria-label="Log out">{String(user?.name || user?.email || 'U').charAt(0).toUpperCase()}</button>
      </header>

      <section className="mobile-pos__invoice-strip">
        <div><small>Invoice</small>{automaticInvoice ? <strong>{salesInvoice}</strong> : <input value={manualInvoice} onChange={(event) => setManualInvoice(event.target.value)} placeholder="Invoice number" />}</div>
        <div><small>Date</small><strong>{today()}</strong></div>
      </section>

      <section className="mobile-pos__search"><FiSearch /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search product or scan barcode" autoComplete="off" /><span>{visibleProducts.length}</span></section>

      <section className="mobile-pos__content">
        {loading ? <div className="mobile-pos__loading"><span></span><p>Loading products…</p></div> : <div className="mobile-pos__product-grid">{visibleProducts.map((product, index) => <button type="button" key={product._id || product.code} className="mobile-pos__product" onClick={() => addProduct(product)}><div className={`mobile-pos__product-icon icon-${index % 6}`}><FiBox /></div><strong>{product.name || 'Unnamed product'}</strong><small>{product.code || 'No code'}</small><b>{money(product.sell)}</b><span><FiPlus /> Add</span></button>)}</div>}
        {!loading && !visibleProducts.length && <div className="mobile-pos__empty"><FiSearch /><p>No matching products found.</p></div>}
      </section>

      <nav className="mobile-pos__bottom-nav">
        <button type="button" className="is-active"><FiShoppingBag /><span>Sell</span></button>
        <button type="button" onClick={() => navigate('/mobile/managedefaultsales')}><MdOutlineReceiptLong /><span>Sales</span></button>
        <button type="button" onClick={() => navigate('/mobile/addmaincustomer')}><FiUser /><span>Customers</span></button>
        <button type="button" onClick={() => navigate('/mobile')}><FiBox /><span>More</span></button>
      </nav>

      <button type="button" className="mobile-pos__cart-bar" onClick={() => setCartOpen(true)}><span className="mobile-pos__cart-badge">{cartQuantity}</span><FiShoppingBag /><div><small>Current cart</small><strong>{money(cartTotal)}</strong></div><span>Review</span></button>

      {cartOpen && <div className="mobile-pos__sheet-backdrop" onMouseDown={() => setCartOpen(false)}><section className="mobile-pos__sheet" onMouseDown={(event) => event.stopPropagation()}><header><div><small>{cartQuantity} item{cartQuantity === 1 ? '' : 's'}</small><h2>Current sale</h2></div><button type="button" onClick={() => setCartOpen(false)} aria-label="Close cart"><FiX /></button></header>
        <div className="mobile-pos__customer"><button type="button" onClick={() => setCustomerPickerOpen((value) => !value)}><FiUser /><div><small>Customer</small><strong>{selectedCustomer?.name || 'Select customer'}</strong></div><FiChevronDown /></button>{customerPickerOpen && <div className="mobile-pos__customer-picker"><input value={customerSearch} onChange={(event) => setCustomerSearch(event.target.value)} placeholder="Find a customer" autoFocus />{visibleCustomers.map((customer) => <button type="button" key={customer._id || customer.code} onClick={() => { setSelectedCustomer(customer); setCustomerPickerOpen(false); }}><span>{customer.name}</span><small>{customer.phone || customer.code}</small></button>)}</div>}</div>
        <div className="mobile-pos__cart-items">{cart.map((item) => <article key={item._id || item.code}><div className="mobile-pos__cart-item-icon"><FiBox /></div><div className="mobile-pos__cart-item-info"><strong>{item.name}</strong><small>{money(item.sell)} each</small></div><div className="mobile-pos__stepper"><button type="button" onClick={() => setQuantity(item, item.quantity - 1)}><FiMinus /></button><b>{item.quantity}</b><button type="button" onClick={() => setQuantity(item, item.quantity + 1)}><FiPlus /></button></div><strong>{money(Number(item.sell || 0) * item.quantity)}</strong></article>)}</div>
        {!cart.length && <div className="mobile-pos__empty"><FiShoppingBag /><p>Your cart is empty.</p></div>}
        <footer><div><span>Total</span><strong>{money(cartTotal)}</strong></div><button type="button" disabled={!cart.length || submitting} onClick={completeSale}>{submitting ? 'Completing sale…' : 'Complete sale'}</button></footer>
      </section></div>}
    </main>
  );
}

export default MobilePos;
